/**
 * Phone quality rules shared by the CRM forms, the customer API and the
 * pipeline. Staff should record either a real number or an honest blank
 * ("Don't have it yet"); placeholders like 000 hide a missing number, spoil
 * Meta match quality and can make different customers look like the same one.
 *
 * The fake-pattern rules mirror the landing page's shared/contact-spam-rules.ts
 * (approved 31 Oct 2026). Overseas numbers are never judged by the UK rules.
 */

export const PHONE_REQUIRED_MESSAGE =
  "Enter the customer's phone number, or tick “Don't have it yet”.";
export const PHONE_FAKE_MESSAGE =
  "That doesn't look like a real phone number. Tick “Don't have it yet” if you don't have it.";
export const PHONE_FAKE_BLANK_MESSAGE =
  "That doesn't look like a real phone number. Leave it blank if you don't have it yet.";

export type FakePhoneReason =
  | 'placeholder'
  | 'repeated_digits'
  | 'long_digit_run'
  | 'counting_sequence'
  | 'ofcom_drama_number'
  | 'premium_rate'
  | 'personal_number'
  | 'pager'
  | 'unallocated_range';

const PHONE_INPUT_PATTERN = /^[+0-9() .-]+$/;
const UK_E164_PATTERN = /^\+44[1-9]\d{9}$/;

/** UK number in E.164, or null (same semantics as the lead-enquiry normalizer). */
export function normalizeUkPhone(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 50 || !PHONE_INPUT_PATTERN.test(trimmed)) return null;
  const digits = trimmed.replace(/\D/g, '');
  let national: string;
  if (trimmed.startsWith('00')) {
    if (!digits.startsWith('0044')) return null;
    national = digits.slice(4);
  } else if (trimmed.startsWith('+')) {
    if (!digits.startsWith('44')) return null;
    national = digits.slice(2);
  } else if (digits.startsWith('44')) {
    national = digits.slice(2);
  } else if (digits.startsWith('0')) {
    national = digits.slice(1);
  } else {
    national = digits;
  }
  if (national.length === 11 && national.startsWith('0')) national = national.slice(1);
  const e164 = `+44${national}`;
  return UK_E164_PATTERN.test(e164) ? e164 : null;
}

const ASCENDING = '0123456789';
const DESCENDING = '9876543210';
const COUNTING_RUN = 7;
const OFCOM_DRAMA_PREFIXES = [
  '7700900',
  '1632960',
  '2079460',
  '1134960',
  '1144960',
  '1154960',
  '1164960',
  '1174960',
  '1184960',
  '1214960',
  '1314960',
  '1414960',
  '1514960',
  '1614960',
  '1914980',
  '2896496',
  '2920180',
  '8081570',
  '9098790',
  '3069990',
];

function hasCountingRun(digits: string): boolean {
  for (let start = 0; start + COUNTING_RUN <= ASCENDING.length; start += 1) {
    if (digits.includes(ASCENDING.slice(start, start + COUNTING_RUN))) return true;
    if (digits.includes(DESCENDING.slice(start, start + COUNTING_RUN))) return true;
  }
  return false;
}

/** Why a supplied phone cannot be a customer's real number, or null. Blank is not fake. */
export function findFakePhoneReason(value: unknown): FakePhoneReason | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 10 || /^(\d)\1*$/.test(digits)) return 'placeholder';

  const e164 = normalizeUkPhone(trimmed);
  if (!e164) return null; // overseas or unusual format: not judged here
  const national = e164.slice(3);
  if (/^(\d)\1+$/.test(national.slice(1))) return 'repeated_digits';
  if (/(\d)\1{6,}/.test(national)) return 'long_digit_run';
  if (hasCountingRun(national)) return 'counting_sequence';
  if (OFCOM_DRAMA_PREFIXES.some((prefix) => national.startsWith(prefix)))
    return 'ofcom_drama_number';
  if (national.startsWith('9')) return 'premium_rate';
  if (national.startsWith('70')) return 'personal_number';
  if (national.startsWith('76') && !national.startsWith('7624')) return 'pager';
  if (national.startsWith('4') || national.startsWith('6')) return 'unallocated_range';
  return null;
}

/** True when there is no usable number: blank, or a placeholder/fake. */
export function isPhoneMissing(value: unknown): boolean {
  if (value === null || value === undefined || !String(value).trim()) return true;
  return findFakePhoneReason(value) !== null;
}

/**
 * Form rule. `required` forms (pipeline Add lead) need a real number or the
 * "Don't have it yet" tick; optional forms only refuse fakes. A number that is
 * unchanged from `initialPhone` is never re-judged, so old records stay editable.
 */
export function validateStaffPhone(input: {
  phone: unknown;
  phoneUnknown?: boolean;
  initialPhone?: unknown;
  required?: boolean;
}): string | null {
  const required = input.required ?? true;
  if (input.phoneUnknown) return null;
  const trimmed =
    input.phone === null || input.phone === undefined ? '' : String(input.phone).trim();
  if (!trimmed) return required ? PHONE_REQUIRED_MESSAGE : null;
  if (
    input.initialPhone !== null &&
    input.initialPhone !== undefined &&
    trimmed === String(input.initialPhone).trim()
  ) {
    return null;
  }
  if (!findFakePhoneReason(trimmed)) return null;
  return required ? PHONE_FAKE_MESSAGE : PHONE_FAKE_BLANK_MESSAGE;
}
